import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listClassRoutines, uploadClassRoutine, deleteClassRoutine } from "../../api/classRoutineApi";
import { fetchFileBlobUrl } from "../../api/fileApi";
import "../Attendance/Attendance.css";

/**
 * Direct port of digicampusbd's `class-routine` ("Class Routine Import") —
 * upload a single PDF timetable document per class-configuration, list what
 * has been uploaded, view it inline, delete it. A completely separate
 * feature from the structured routine builder (`ClassRoutineBuilderPage`)
 * despite the similar name — this is a plain file upload, matching the old
 * app's own two-table split (`class_routine` vs `class_routine_details`).
 * Reuses the app's existing generic `/api/files` store/serve pair (already
 * used for student/staff photos) under a new "class-routines" folder,
 * rather than the old app's own separate on-disk `FileStorageService`
 * folder — same data, same behavior, this app's own generic infra.
 */
export default function ClassRoutineImportPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [file, setFile] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadRoutines = () => {
    setLoading(true);
    listClassRoutines()
      .then(setRoutines)
      .catch(() => setError("Failed to load class routines."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    loadRoutines();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!classConfigurationId) {
      setError("Please select a section.");
      return;
    }
    if (!file) {
      setError("Please choose a PDF file.");
      return;
    }
    setUploading(true);
    try {
      await uploadClassRoutine(classConfigurationId, file);
      setSuccess("File successfully saved.");
      setFile(null);
      loadRoutines();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to upload file.");
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (fileName) => {
    try {
      const url = await fetchFileBlobUrl("class-routines", fileName);
      window.open(url, "_blank");
    } catch (err) {
      setError("Failed to load the routine file.");
    }
  };

  const handleDelete = async (routineId) => {
    setError("");
    setSuccess("");
    try {
      await deleteClassRoutine(routineId);
      setSuccess("File successfully deleted.");
      loadRoutines();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete file.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Class Routine</h1>
      </div>

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      <form className="attendance-filters" onSubmit={handleUpload}>
        <label>
          Select Section
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Upload file (PDF)
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} />
        </label>
        <button type="submit" className="btn-primary" disabled={uploading}>
          {uploading ? "Saving..." : "Save"}
        </button>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : routines.length === 0 ? (
        <p>No routines uploaded yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Section Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {routines.map((r) => (
                <tr key={r.routineId}>
                  <td>{r.classConfigName}</td>
                  <td>
                    <button type="button" className="btn-secondary" onClick={() => handleView(r.fileName)}>
                      View
                    </button>{" "}
                    <button type="button" className="btn-danger" onClick={() => handleDelete(r.routineId)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </MainLayout>
  );
}
